import { api } from './api-client';

/**
 * The headlines the first run offers, as `/v1/admin/headlines` serves them.
 *
 * Declared here rather than imported from the API — ADR 0001 duplicates types
 * between the API and its clients rather than sharing them through a workspace.
 */
export interface Headline {
  id: string;
  /** As published. Not re-cased or re-worded. */
  title: string;
  /** Who published it. */
  source: string;
  /** When it was published, not when it was added. ISO 8601. */
  publishedAt: string;
  /**
   * What this headline says about somebody who clicks it.
   *
   * May be empty, and an empty one is the thing this screen exists to make
   * visible: an untagged headline can still be offered and still be picked, and
   * the answer it produces carries nothing. Legal, and almost always an
   * oversight.
   */
  interests: HeadlineTag[];
  createdAt: string;
  updatedAt: string;
}

/** One interest a headline carries. */
export interface HeadlineTag {
  id: string;
  code: string;
  label: string;
}

/** What the add and edit forms produce. */
export interface HeadlineDraft {
  title: string;
  source: string;
  publishedAt: string;
  /** By id, and the whole set: what is sent replaces what is stored. */
  interestIds: string[];
}

export async function listHeadlines(): Promise<Headline[]> {
  const { headlines } = await api.get<{ headlines: Headline[] }>(
    '/admin/headlines',
  );
  return headlines;
}

export async function createHeadline(draft: HeadlineDraft): Promise<Headline> {
  const { headline } = await api.post<{ headline: Headline }>(
    '/admin/headlines',
    draft,
  );
  return headline;
}

export async function updateHeadline(
  id: string,
  draft: HeadlineDraft,
): Promise<Headline> {
  const { headline } = await api.patch<{ headline: Headline }>(
    `/admin/headlines/${id}`,
    draft,
  );
  return headline;
}

export function deleteHeadline(id: string): Promise<void> {
  return api.del(`/admin/headlines/${id}`);
}

/**
 * How old a headline is, in words.
 *
 * The portal says this out loud because the catalogue is curated by hand until
 * a refresh job exists, and the first run's copy implies these are recent. A
 * table full of three-month-old stories should be obvious at a glance.
 */
export function age(publishedAt: string): string {
  const hours = Math.floor(
    (Date.now() - new Date(publishedAt).getTime()) / 3_600_000,
  );
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}
